import { useState, type CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, FolderOpen, Bot, Settings, ListChecks, Users, PanelLeftClose, PanelLeftOpen, ChevronRight, LogOut,
} from 'lucide-react'
import styles from './AppShell.module.css'

export interface AppShellUser {
  name: string | null | undefined
  role: string
}

export interface AppShellProps {
  navMode: 'client' | 'admin'
  user?: AppShellUser | null
  onLogout?: () => void
  children: React.ReactNode
}

const CLIENT_NAV = [
  { to: '/dashboard', label: 'Analysen', Icon: LayoutDashboard, end: true },
  { to: '/symon', label: 'Symon', Icon: Bot, end: true },
  { to: '/settings', label: 'Einstellungen', Icon: Settings, end: true },
]

const ADMIN_NAV = [
  { to: '/admin/projects', label: 'Projekte', Icon: FolderOpen, end: false },
  { to: '/admin/activity', label: 'Aktivitätslog', Icon: ListChecks, end: true },
  { to: '/admin/users', label: 'Benutzer', Icon: Users, end: false },
]

const ADMIN_ACCOUNT_NAV = [
  { to: '/dashboard', label: 'Analysen', Icon: LayoutDashboard, end: true },
  { to: '/settings', label: 'Einstellungen', Icon: Settings, end: true },
]

function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return name.split(' ').map(p => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?'
}

export function AppShell({ navMode, user, onLogout, children }: AppShellProps) {
  const [open, setOpen] = useState<boolean>(() => {
    const stored = sessionStorage.getItem('rb_client_sidebar_open')
    return stored === null ? true : stored === 'true'
  })

  function toggleSidebar() {
    setOpen(prev => {
      const next = !prev
      sessionStorage.setItem('rb_client_sidebar_open', String(next))
      return next
    })
  }

  const sidebarW = open ? 'var(--nav-rail-width)' : '56px'

  return (
    <div className={styles.shell} style={{ '--_sidebar-w': sidebarW } as CSSProperties}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerBrand} style={{ width: sidebarW }}>
          {open ? (
            <>
              <img className={`${styles.brandLogo} ${styles.logoDark}`} src="/symon-dark-mode.svg" alt="Symon" />
              <img className={`${styles.brandLogo} ${styles.logoLight}`} src="/symon-light-mode.svg" alt="Symon" />
            </>
          ) : (
            <>
              <img className={`${styles.brandSignet} ${styles.logoDark}`} src="/symon-signet-dark-mode.svg" alt="Symon" />
              <img className={`${styles.brandSignet} ${styles.logoLight}`} src="/symon-signet-light-mode.svg" alt="Symon" />
            </>
          )}
        </div>

        <div className={styles.headerRight}>
          {user && (
            <div className={styles.headerAvatar} title={user.name ?? undefined}>
              {initials(user.name)}
            </div>
          )}
        </div>
      </header>

      {/* Sidebar */}
      <aside className={open ? styles.sidenav : `${styles.sidenav} ${styles.sidenavCollapsed}`}>
        {navMode === 'admin' ? (
          <>
            <div className={styles.section}>
              <div className={open ? styles.eyebrow : `${styles.eyebrow} ${styles.eyebrowHidden}`}>
                Admin
              </div>
              {ADMIN_NAV.map(({ to, label, Icon, end }) => (
                <NavLink
                  key={label}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    [styles.navItem, !open && styles.navItemCollapsed, isActive && styles.navItemActive].filter(Boolean).join(' ')
                  }
                  title={!open ? label : undefined}
                >
                  <span className={styles.navIcon}><Icon size={18} strokeWidth={1.5} /></span>
                  <span className={open ? styles.navLabel : `${styles.navLabel} ${styles.navLabelHidden}`}>{label}</span>
                </NavLink>
              ))}
            </div>
            <div className={styles.section}>
              <div className={open ? styles.eyebrow : `${styles.eyebrow} ${styles.eyebrowHidden}`}>
                Konto
              </div>
              {ADMIN_ACCOUNT_NAV.map(({ to, label, Icon, end }) => (
                <NavLink
                  key={label}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    [styles.navItem, !open && styles.navItemCollapsed, isActive && styles.navItemActive].filter(Boolean).join(' ')
                  }
                  title={!open ? label : undefined}
                >
                  <span className={styles.navIcon}><Icon size={18} strokeWidth={1.5} /></span>
                  <span className={open ? styles.navLabel : `${styles.navLabel} ${styles.navLabelHidden}`}>{label}</span>
                </NavLink>
              ))}
            </div>
          </>
        ) : (
          <div className={styles.section}>
            {CLIENT_NAV.map(({ to, label, Icon, end }) => (
              <NavLink
                key={label}
                to={to}
                end={end}
                className={({ isActive }) =>
                  [styles.navItem, !open && styles.navItemCollapsed, isActive && styles.navItemActive].filter(Boolean).join(' ')
                }
                title={!open ? label : undefined}
              >
                <span className={styles.navIcon}><Icon size={18} strokeWidth={1.5} /></span>
                <span className={open ? styles.navLabel : `${styles.navLabel} ${styles.navLabelHidden}`}>{label}</span>
              </NavLink>
            ))}
          </div>
        )}

        <div className={styles.sidenavFooter}>
          <button
            className={open ? styles.collapseBtn : `${styles.collapseBtn} ${styles.collapseBtnCollapsed}`}
            onClick={toggleSidebar}
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className={styles.navIcon}>
              {open ? <PanelLeftClose size={18} strokeWidth={1.5} /> : <PanelLeftOpen size={18} strokeWidth={1.5} />}
            </span>
            {open && <span>Collapse</span>}
          </button>

          {onLogout && (
            <button
              className={open ? styles.collapseBtn : `${styles.collapseBtn} ${styles.collapseBtnCollapsed}`}
              onClick={onLogout}
              aria-label="Abmelden"
            >
              <span className={styles.navIcon}><LogOut size={18} strokeWidth={1.5} /></span>
              {open && <span>Abmelden</span>}
            </button>
          )}

          {user && (
            <div className={open ? styles.userRow : `${styles.userRow} ${styles.navItemCollapsed}`} title={user.name ?? undefined}>
              <div className={styles.avatar}>{initials(user.name)}</div>
              <div className={open ? styles.userInfo : `${styles.userInfo} ${styles.userInfoHidden}`}>
                <span className={styles.userName}>{user.name ?? ''}</span>
                <span className={styles.userRole}>{user.role}</span>
              </div>
              {open && <ChevronRight size={14} strokeWidth={1.5} style={{ color: 'var(--fg-subtle)', flexShrink: 0 }} />}
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className={styles.content}>
        {children}
      </main>
    </div>
  )
}
